import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Layers, Users, MessageSquare, Palette, Factory, Truck } from 'lucide-react';
import OrderCTA from '../components/OrderCTA';
import homeBgBanner from '../assets/homebgbanner.webp';
import homePremium from '../assets/home_premium.webp';

const CountUp = ({ end, suffix = '', duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const countRef = React.useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                    setCount(0); // Reset count when out of view
                }
            },
            { threshold: 0.1 }
        );

        if (countRef.current) {
            observer.observe(countRef.current);
        }

        return () => {
            if (countRef.current) {
                observer.unobserve(countRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        let startTime = null;
        let animationFrameId;

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = currentTime - startTime;

            if (progress < duration) {
                const ease = 1 - Math.pow(1 - progress / duration, 3);
                setCount(Math.min(end, Math.floor(ease * (end + 1))));
                animationFrameId = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        animationFrameId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isVisible, end, duration]);

    return <span ref={countRef}>{count}{suffix}</span>;
};

const Home = () => {
    return (
        <div className="bg-secondary">
            {/* Hero Section */}
            <section
                className="relative text-white py-40 px-4 sm:px-6 lg:px-8 text-center overflow-hidden bg-cover bg-bottom min-h-[80vh] flex flex-col justify-center"
                style={{ backgroundImage: `url(${homeBgBanner})` }}
            >
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <div className="relative z-10 max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 font-serif text-secondary">
                        Thread and Needle Valley
                    </h1>
                    <p className="text-xl md:text-2xl mb-10 text-green-50 font-light">
                        Your Trusted Partner in Premium Custom Textile Manufacturing.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Link to="/collection" className="bg-primary text-secondary px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition shadow-lg">
                            Explore Collection
                        </Link>
                        <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white hover:text-primary transition">
                            Get a Quote
                        </Link>
                    </div>
                </div>
            </section>

            {/* About Section Snippet */}
            <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Stats Widget */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 border-b border-gray-200 pb-12">
                    <div className="text-center">
                        <span className="block text-4xl font-bold text-primary mb-2">
                            <CountUp end={7} suffix="+" />
                        </span>
                        <span className="text-gray-500 text-sm uppercase tracking-wide">Years Experience</span>
                    </div>
                    <div className="text-center">
                        <span className="block text-4xl font-bold text-primary mb-2">
                            <CountUp end={100} suffix="+" />
                        </span>
                        <span className="text-gray-500 text-sm uppercase tracking-wide">Projects Done</span>
                    </div>
                    <div className="text-center">
                        <span className="block text-4xl font-bold text-primary mb-2">
                            <CountUp end={100} suffix="%" />
                        </span>
                        <span className="text-gray-500 text-sm uppercase tracking-wide">Quality Guarantee</span>
                    </div>
                    <div className="text-center">
                        <span className="block text-4xl font-bold text-primary mb-2">
                            <CountUp end={24} suffix="/7" />
                        </span>
                        <span className="text-gray-500 text-sm uppercase tracking-wide">Support</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div className="space-y-6">
                        <h2 className="text-3xl font-bold text-[#228f6e] font-serif">About Our Craft</h2>
                        <p className="text-lg text-gray-600 leading-relaxed">
                            We define excellence in textile manufacturing. From bespoke bedsheets to luxury towels, we bring your vision to life with precision and care. Whether you are a boutique brand or a large enterprise, we customize every thread to match your specific requirements.
                        </p>
                        <Link to="/about" className="inline-block text-primary font-bold border-b-2 border-primary hover:text-[#1a7157] transition">
                            Know More About Us &rarr;
                        </Link>
                    </div>
                    <div className="rounded-lg overflow-hidden h-80 shadow-lg group">
                        <img
                            src={homePremium}
                            alt="Premium Manufacturing"
                            className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                        />
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-accent py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-primary mb-16 font-serif">Why Choose Thread and Needle Valley?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:-translate-y-1">
                            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-primary">
                                <Layers size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">Custom Manufacturing</h3>
                            <p className="text-gray-600">
                                We don't just sell products; we create them. Any color, any pattern, any size—manufactured exactly to your specs.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:-translate-y-1">
                            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-primary">
                                <Award size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">Premium Quality</h3>
                            <p className="text-gray-600">
                                We use only the finest materials to ensure durability, softness, and a luxurious feel that your customers will love.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:-translate-y-1">
                            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-primary">
                                <Users size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">B2B Partner</h3>
                            <p className="text-gray-600">
                                Reliable, scalable, and professional. We are the manufacturing partner your business can count on for bulk orders.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Manufacturing Process */}
            {/* Manufacturing Process */}
            <section className="bg-accent py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-primary mb-8 font-serif">How We Work</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-green-200 -z-10 transform -translate-y-1/2 opacity-50"></div>

                        <div className="text-center relative p-4">
                            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-4 border-white">
                                <MessageSquare className="text-primary" size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">1. Consultation</h3>
                            <p className="text-gray-600 text-sm">We discuss your requirements, selecting fabrics, and defining specifications.</p>
                        </div>

                        <div className="text-center relative p-4">
                            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-4 border-white">
                                <Palette className="text-primary" size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">2. Sampling</h3>
                            <p className="text-gray-600 text-sm">We create a sample for your approval to ensure everything is perfect.</p>
                        </div>

                        <div className="text-center relative p-4">
                            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-4 border-white">
                                <Factory className="text-primary" size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">3. Production</h3>
                            <p className="text-gray-600 text-sm">Our artisans manufacture your bulk order with precision and care.</p>
                        </div>

                        <div className="text-center relative p-4">
                            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-4 border-white">
                                <Truck className="text-primary" size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-dark">4. Delivery</h3>
                            <p className="text-gray-600 text-sm">We package and ship your products securely to your doorstep.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <OrderCTA title="Start Your Custom Production Today" />
        </div>
    );
};

export default Home;
